import groupBy from 'lodash.groupby';

import { createSlice } from '@reduxjs/toolkit';

import { ApplicationStepStatusEnum } from '../../enum/applicationStepStatus';
import {
  getApplicantFilesThunk,
  getApplicationCreditRecordThunk,
  getApplicationDetailsThunk,
  getApplicationsThunk,
  getHistoryLogsByApplicationIDAndAgreementIDThunk,
  getHistoryLogsByApplicationIDThunk,
  getInternalRemarksByApplicationIDThunk,
  globalSearchThunk,
  getApplicationRecordByApplicationIdThunk,
} from '../../services/applicationService/applicationThunk';
import {
  getSubmitForApprovalIslamicApplicationsThunk,
  getPendingIslamicApplicationsThunk,
  getReferbackIslamicApplicationsThunk,
  getReferbackFromPendingDisbursementIslamicApplicationsThunk,
  getFollowUpIslamicApplicationsThunk,
  getApprovedIslamicApplicationsThunk,
  getRejectedIslamicApplicationsThunk,
  getCancelledIslamicApplicationsThunk,
  getDraftIslamicApplicationsThunk,
  getIslamicApprovedReviewApplicationsThunk,
} from '../../services/islamicApplicationService/islamicApplicationThunk';
import {
  getEkycFilesByEntityIdThunk,
  getFilesByEntityIdThunk,
} from '../../services/fileService/fileThunk';
import { Agreement } from '../agreement/agreementSlice';
import { User } from '../user/userSlice';

export type LoanReason = {
  id: string;
  adminId: string;
  code: string;
  name: string;
  createdAt?: string;
  updatedAt?: string;
};

export type EmployeeType = {
  id: string;
  adminId: string;
  code: string;
  name: string;
  createdAt?: string;
  updatedAt?: string;
};

export type Bank = {
  id: string;
  adminId: string;
  code: string;
  name: string;
  createdAt?: string;
  updatedAt?: string;
};

export type Relationship = {
  id: string;
  adminId: string;
  code: string;
  name: string;
  createdAt?: string;
  updatedAt?: string;
};

export type Product = {
  id: string;
  adminId: string;
  name: string;
  code: string;
  minAgeChecking: number;
  maxAgeChecking: number;
  minGrossIncomeChecking: number;
  interestRate: number;
  minTenure: number;
  maxTenure: number;
  intervalTenure: number;
  referralCode: string;
  createdAt?: string;
  updatedAt?: string;
  startDate?: string;
  expiredDate?: string;
};

export type BankruptcyCheck = { bankruptcyCheck: string };
export type Amla = {
  id: string;
  proId: string;
  gdr: string;
  matSco: string;
  matNam: string;
  cty: string;
  tit: string;
  cddiId: string;
  createdAt: string;
  updatedAt: string;
};
export type IScore = { iScore: string };
export type SpecialAttentionFlag = { specialAttentionAccountCount: string };
export type CcrisStatusCode = { ccrisStatusCode: string };
export type CcrisLegal = { ccrisLegal: string };
export type TradeReference = {
  id: string;
  subject: string;
  newIc: string;
  creditorName: string;
  creditorTelNo: string;
  refNo: string;
  status: string;
  debtType: string;
  paymentAging: string;
  remark: string;
  statusDate: string;
  amount: string;
  spkccsId: string;
  createdAt: string;
  updatedAt: string;
};

export type IslamicApplication = {
  applicationFormFileId?: string;
  applicantAcceptTncAndPp?: boolean;
  applicantAcceptMarketing?: boolean;
  applicantAcceptSharePersonalInfo?: boolean;
  applicantEmergencyContactRelationship?: string;
  applicantEmploymentType?: string;
  applicationType?: string;

  productName?: string;
  productCode?: string;
  productMinAgeChecking?: number;
  productMaxAgeChecking?: number;
  productMinGrossIncomeChecking?: number;
  productInterestRate?: number;
  productMinTenure?: number;
  productMaxTenure?: number;
  productIntervalTenure?: number;
  productStampingDutyPercentage?: number;
  productStampingFee?: number;
  productProcessingFee?: number;
  productReferralCode?: string;
  productProcessingFeeToDiscount?: number;

  numberOfCcrisReport?: number;

  applicantBankName?: string;
  applicantPersonalBankNo?: string;
  applicantFirstName?: string;
  applicantNric?: string;
  applicantPhoneNo?: string;
  applicantAddress1?: string;
  applicantAddress2?: string;
  applicantAddress3?: string;
  applicantCity?: string;
  applicantPostcode?: string;
  applicantState?: string;
  applicantEmail?: string;
  applicantGrossSalaryRange?: string;
  applicantRace?: string;
  applicantPartnerId?: string;
  experianSpkccsFileId?: string;
  experianIrissFileId?: string;
  experianNvrbFileId?: string;
  experianAmlaFileId?: string;
  customerType: string;
  loanType: string;
  id?: string;
  applicationNo?: string;
  userId?: string;
  adminId: string;
  agreementId?: string;
  productId?: string;
  applicationStatus?: string;
  remarks?: string;
  loanReasonId?: string;
  employmentTypeId?: string;
  personalBankId?: string;
  personalBankAccountNo?: string;
  actualMonthlySalary?: number;
  employerName?: string;
  employerContactNo?: string;
  jobTitle?: string;
  emergencyContactName?: string;
  emergencyContactNo?: string;
  emergencyContactRelationshipId?: string;
  emergencyContactPersonEmail?: string;
  secondEmergencyContactName?: string;
  secondEmergencyContactNo?: string;
  secondEmergencyContactRelationshipId?: string;
  secondEmergencyContactPersonEmail?: string;
  applicantSecondEmergencyContactRelationship?: string;
  employmentStartingFrom?: string;
  loanApplied: number;
  eligibleLoanAmount?: number;
  financeAmount?: number;
  loanTenure: number;
  loanInterestRate: number;
  submissionDate: string;
  dsr?: number;
  bucket?: string;
  createdAt?: string;
  updatedAt?: string;
  user?: User;
  agreement?: Agreement;
  admin?: { username?: string };
  product?: Product;
  loanReason?: LoanReason;
  employmentType?: EmployeeType;
  personalBank?: Bank;
  emergencyContactRelationship?: Relationship;
  bankruptcyCheck?: BankruptcyCheck[];
  amla?: Amla[];
  iScore?: IScore[];
  specialAttentionFlag?: SpecialAttentionFlag[];
  ccrisStatusCode?: CcrisStatusCode[];
  ccrisLegal?: CcrisLegal[];
  ccrisMia?: any;
  tradeReference?: TradeReference[];
  applicantBankIbgRoutingNumber?: string;
  applicantLoanReason?: string;
  isCustomReason: boolean;
  customReason?: string;
  language?: string;
  verifierSubmitDate?: string;
  ApplicationStepStatus?: ApplicationStepStatus[];
  ocrTaskRef?: string;
  detailedStatus?: string;
  currentEmploymentDuration?: string;
  loanTotalPremium?: number;
};

export type CreditRecord = {
  id: string;
  companyId: string;
  applicationId: string;
  age: string;
  gross_income: string;
  bankruptcy_check: string;
  amla: string;
  iscore: string;
  iscore_value: number;
  dsr_value: number;
  risk_profile: string;
  special_attention_flag: string;
  ccris_status_code: string;
  ccris_legal: string;
  mia_in_ccris_two_times: string;
  mia_in_ccris_three_times: string;
  trade_reference_amount: string;
  experian_legal: string;
  createdAt: string;
  updatedAt: string;
};

export type UploadedFile = {
  id: string;
  entityId: string;
  filename: string;
  originalName: string;
  mimeType: string;
  fileTypeId: string;
  fileType: {
    id: string;
    name: string;
    description?: string;
    fileSizeLimit?: number;
    createdAt?: string;
    updatedAt?: string;
  };
  createdAt: string;
  updatedAt: string;
};

export type HistoryLog = {
  id: string;
  userId: string;
  user?: User;
  description?: string;
  adminId: string;
  applicationId?: string;
  application?: IslamicApplication;
  agreementId?: string;
  agreement?: Agreement;
  createdAt: string;
  updatedAt: string;
};

export type InternalRemark = {
  id: string;
  userId: string;
  user: User;
  applicationId?: string;
  remarks: string;
  description: string;
  createdAt: string;
  updatedAt: string;
};

export type ApplicationStepStatus = {
  id: string;
  name: ApplicationStepStatusEnum;
  status: boolean;
  applicationId: string;
  createdAt: string;
  updatedAt: string;
};

export type ApplicationListing = {
  searchTotal: number;
  total: number;
  currentPage: number;
  pageSize: number;
  data: IslamicApplication[];
};

export type ApplicationState = {
  applicationList: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: ApplicationListing;
  };
  submitForApprovalIslamicApplicationList: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: ApplicationListing;
  };
  pendingIslamicApplicationList: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: ApplicationListing;
  };
  approvedIslamicApplicationList: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: ApplicationListing;
  };
  followUpIslamicApplicationList: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: ApplicationListing;
  };
  rejectIslamicApplicationList: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: ApplicationListing;
  };
  cancelledIslamicApplicationList: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: ApplicationListing;
  };
  draftIslamicApplicationList: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: ApplicationListing;
  };
  referBackIslamicApplicationList: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: ApplicationListing;
  };
  referBackFromPendingDisbursementIslamicApplicationList: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: ApplicationListing;
  };
  creditRecords: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: CreditRecord | undefined;
  };
  applicationDetails: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data?: IslamicApplication;
  };
  applicationFiles: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: {
      salaryslip?: UploadedFile[];
      bankstatement?: UploadedFile[];
      othersrelevantdocuments?: UploadedFile[];
    };
  };
  ekycFiles: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: UploadedFile[];
  };
  applicantFiles: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: UploadedFile[];
  };
  historyLogs: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data?: HistoryLog[];
  };
  internalRemarks: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data?: InternalRemark[];
  };
  globalSearchApplicationList: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data?: ApplicationListing;
  };
  globalSearchValue: {
    searchType: string;
    value: string;
  };
  accountStatementSearchValue: {
    searchType: string;
    value: string;
  };
  approvedReviewIslamicApplicationList: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data?: ApplicationListing;
  };
  applicationRecord: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: {
      pagination: {
        total: number;
        current: number;
        pageSize: number;
      };
      data: (IslamicApplication & { agreement: Agreement | null })[];
    };
  };
};

const initialState: ApplicationState = {
  applicationList: {
    isLoading: false,
    isError: false,
    errorMessage: '',
    data: {
      searchTotal: 0,
      total: 0,
      currentPage: 0,
      pageSize: 0,
      data: [],
    },
  },
  submitForApprovalIslamicApplicationList: {
    isLoading: false,
    isError: false,
    errorMessage: '',
    data: {
      searchTotal: 0,
      total: 0,
      currentPage: 0,
      pageSize: 0,
      data: [],
    },
  },
  creditRecords: {
    isLoading: false,
    isError: false,
    errorMessage: '',
    data: undefined,
  },
  applicationDetails: {
    isLoading: false,
    isError: false,
    errorMessage: '',
    data: undefined,
  },
  applicationFiles: {
    isLoading: false,
    isError: false,
    errorMessage: '',
    data: {
      salaryslip: [],
      bankstatement: [],
      othersrelevantdocuments: [],
    },
  },
  applicantFiles: {
    isLoading: false,
    isError: false,
    errorMessage: '',
    data: [],
  },
  ekycFiles: {
    isLoading: false,
    isError: false,
    errorMessage: '',
    data: [],
  },
  historyLogs: {
    isLoading: false,
    isError: false,
    errorMessage: '',
    data: [],
  },
  internalRemarks: {
    isLoading: false,
    isError: false,
    errorMessage: '',
    data: [],
  },
  pendingIslamicApplicationList: {
    isLoading: false,
    isError: false,
    errorMessage: '',
    data: {
      searchTotal: 0,
      total: 0,
      currentPage: 0,
      pageSize: 0,
      data: [],
    },
  },
  followUpIslamicApplicationList: {
    isLoading: false,
    isError: false,
    errorMessage: '',
    data: {
      searchTotal: 0,
      total: 0,
      currentPage: 0,
      pageSize: 0,
      data: [],
    },
  },
  referBackFromPendingDisbursementIslamicApplicationList: {
    isLoading: false,
    isError: false,
    errorMessage: '',
    data: {
      searchTotal: 0,
      total: 0,
      currentPage: 0,
      pageSize: 0,
      data: [],
    },
  },
  approvedIslamicApplicationList: {
    isLoading: false,
    isError: false,
    errorMessage: undefined,
    data: {
      searchTotal: 0,
      total: 0,
      currentPage: 0,
      pageSize: 0,
      data: [],
    },
  },
  rejectIslamicApplicationList: {
    isLoading: false,
    isError: false,
    errorMessage: undefined,
    data: {
      searchTotal: 0,
      total: 0,
      currentPage: 0,
      pageSize: 0,
      data: [],
    },
  },
  cancelledIslamicApplicationList: {
    isLoading: false,
    isError: false,
    errorMessage: undefined,
    data: {
      searchTotal: 0,
      total: 0,
      currentPage: 0,
      pageSize: 0,
      data: [],
    },
  },
  draftIslamicApplicationList: {
    isLoading: false,
    isError: false,
    errorMessage: undefined,
    data: {
      searchTotal: 0,
      total: 0,
      currentPage: 0,
      pageSize: 0,
      data: [],
    },
  },
  referBackIslamicApplicationList: {
    isLoading: false,
    isError: false,
    errorMessage: undefined,
    data: {
      searchTotal: 0,
      total: 0,
      currentPage: 0,
      pageSize: 0,
      data: [],
    },
  },
  globalSearchApplicationList: {
    isLoading: false,
    isError: false,
    errorMessage: undefined,
    data: {
      searchTotal: 0,
      total: 0,
      currentPage: 0,
      pageSize: 0,
      data: [],
    },
  },

  globalSearchValue: {
    searchType: 'applicationNo',
    value: '',
  },
  accountStatementSearchValue: {
    searchType: 'CUSTOMER_NRIC',
    value: '',
  },

  approvedReviewIslamicApplicationList: {
    isLoading: false,
    isError: false,
    errorMessage: '',
    data: {
      searchTotal: 0,
      total: 0,
      currentPage: 0,
      pageSize: 0,
      data: [],
    },
  },
  applicationRecord: {
    isLoading: false,
    isError: false,
    errorMessage: '',
    data: {
      pagination: {
        total: 0,
        current: 1,
        pageSize: 10,
      },
      data: [],
    },
  },
};

export const islamicApplicationSlice = createSlice({
  name: 'islamicApplication',
  initialState,
  reducers: {
    applicationStateReset: () => initialState,
    applicationDetailsStateReset: (state) => {
      state.applicationDetails = initialState.applicationDetails;
      state.ekycFiles = initialState.ekycFiles;
      state.applicationFiles = initialState.applicationFiles;
      state.historyLogs = initialState.historyLogs;
      state.internalRemarks = initialState.internalRemarks;
    },
    setApplicationDetails: (state, action) => {
      state.applicationDetails.data = action.payload;
    },
    setGlobalSearchValue: (state, action) => {
      state.globalSearchValue = action.payload;
    },
    setAccountStatementSearchValue: (state, action) => {
      state.accountStatementSearchValue = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getApplicationsThunk.pending, (state) => {
        state.applicationList.isLoading = true;
      })
      .addCase(getApplicationsThunk.fulfilled, (state, action) => {
        state.applicationList.isLoading = false;
        state.applicationList.data = action.payload;
      })
      .addCase(getApplicationsThunk.rejected, (state, action) => {
        state.applicationList.isLoading = false;
        state.applicationList.isError = true;
        state.applicationList.errorMessage = action.payload;
        state.applicationList.data.data = [];
      });

    builder
      .addCase(getSubmitForApprovalIslamicApplicationsThunk.pending, (state) => {
        state.submitForApprovalIslamicApplicationList.isLoading = true;
      })
      .addCase(getSubmitForApprovalIslamicApplicationsThunk.fulfilled, (state, action) => {
        state.submitForApprovalIslamicApplicationList.isLoading = false;
        state.submitForApprovalIslamicApplicationList.data = action.payload;
      })
      .addCase(getSubmitForApprovalIslamicApplicationsThunk.rejected, (state, action) => {
        state.submitForApprovalIslamicApplicationList.isLoading = false;
        state.submitForApprovalIslamicApplicationList.isError = true;
        state.submitForApprovalIslamicApplicationList.errorMessage = action.payload;
        state.submitForApprovalIslamicApplicationList.data.data = [];
      });

    builder
      .addCase(getPendingIslamicApplicationsThunk.pending, (state) => {
        state.pendingIslamicApplicationList.isLoading = true;
      })
      .addCase(getPendingIslamicApplicationsThunk.fulfilled, (state, action) => {
        state.pendingIslamicApplicationList.isLoading = false;
        state.pendingIslamicApplicationList.data = action.payload;
      })
      .addCase(getPendingIslamicApplicationsThunk.rejected, (state, action) => {
        state.pendingIslamicApplicationList.isLoading = false;
        state.pendingIslamicApplicationList.isError = true;
        state.pendingIslamicApplicationList.errorMessage = action.payload;
        state.pendingIslamicApplicationList.data.data = [];
      });

    builder
      .addCase(getApprovedIslamicApplicationsThunk.pending, (state) => {
        state.approvedIslamicApplicationList.isLoading = true;
      })
      .addCase(getApprovedIslamicApplicationsThunk.fulfilled, (state, action) => {
        state.approvedIslamicApplicationList.isLoading = false;
        state.approvedIslamicApplicationList.data = action.payload;
      })
      .addCase(getApprovedIslamicApplicationsThunk.rejected, (state, action) => {
        state.approvedIslamicApplicationList.isLoading = false;
        state.approvedIslamicApplicationList.isError = true;
        state.approvedIslamicApplicationList.errorMessage = action.payload;
        state.approvedIslamicApplicationList.data.data = [];
      });

    builder
      .addCase(getReferbackIslamicApplicationsThunk.pending, (state) => {
        state.referBackIslamicApplicationList.isLoading = true;
      })
      .addCase(getReferbackIslamicApplicationsThunk.fulfilled, (state, action) => {
        state.referBackIslamicApplicationList.isLoading = false;
        state.referBackIslamicApplicationList.data = action.payload;
      })
      .addCase(getReferbackIslamicApplicationsThunk.rejected, (state, action) => {
        state.referBackIslamicApplicationList.isLoading = false;
        state.referBackIslamicApplicationList.isError = true;
        state.referBackIslamicApplicationList.errorMessage = action.payload;
        state.referBackIslamicApplicationList.data.data = [];
      });

    builder
      .addCase(getFollowUpIslamicApplicationsThunk.pending, (state) => {
        state.followUpIslamicApplicationList.isLoading = true;
      })
      .addCase(getFollowUpIslamicApplicationsThunk.fulfilled, (state, action) => {
        state.followUpIslamicApplicationList.isLoading = false;
        state.followUpIslamicApplicationList.data = action.payload;
      })
      .addCase(getFollowUpIslamicApplicationsThunk.rejected, (state, action) => {
        state.followUpIslamicApplicationList.isLoading = false;
        state.followUpIslamicApplicationList.isError = true;
        state.followUpIslamicApplicationList.errorMessage = action.payload;
        state.followUpIslamicApplicationList.data.data = [];
      });

    builder
      .addCase(getRejectedIslamicApplicationsThunk.pending, (state) => {
        state.rejectIslamicApplicationList.isLoading = true;
      })
      .addCase(getRejectedIslamicApplicationsThunk.fulfilled, (state, action) => {
        state.rejectIslamicApplicationList.isLoading = false;
        state.rejectIslamicApplicationList.data = action.payload;
      })
      .addCase(getRejectedIslamicApplicationsThunk.rejected, (state, action) => {
        state.rejectIslamicApplicationList.isLoading = false;
        state.rejectIslamicApplicationList.isError = true;
        state.rejectIslamicApplicationList.errorMessage = action.payload;
        state.rejectIslamicApplicationList.data.data = [];
      });

    builder
      .addCase(getCancelledIslamicApplicationsThunk.pending, (state) => {
        state.cancelledIslamicApplicationList.isLoading = true;
      })
      .addCase(getCancelledIslamicApplicationsThunk.fulfilled, (state, action) => {
        state.cancelledIslamicApplicationList.isLoading = false;
        state.cancelledIslamicApplicationList.data = action.payload;
      })
      .addCase(getCancelledIslamicApplicationsThunk.rejected, (state, action) => {
        state.cancelledIslamicApplicationList.isLoading = false;
        state.cancelledIslamicApplicationList.isError = true;
        state.cancelledIslamicApplicationList.errorMessage = action.payload;
        state.cancelledIslamicApplicationList.data.data = [];
      });

    builder
      .addCase(getDraftIslamicApplicationsThunk.pending, (state) => {
        state.draftIslamicApplicationList.isLoading = true;
      })
      .addCase(getDraftIslamicApplicationsThunk.fulfilled, (state, action) => {
        state.draftIslamicApplicationList.isLoading = false;
        state.draftIslamicApplicationList.data = action.payload;
      })
      .addCase(getDraftIslamicApplicationsThunk.rejected, (state, action) => {
        state.draftIslamicApplicationList.isLoading = false;
        state.draftIslamicApplicationList.isError = true;
        state.draftIslamicApplicationList.errorMessage = action.payload;
        state.draftIslamicApplicationList.data.data = [];
      });

    builder
      .addCase(getReferbackFromPendingDisbursementIslamicApplicationsThunk.pending, (state) => {
        state.referBackFromPendingDisbursementIslamicApplicationList.isLoading = true;
      })
      .addCase(
        getReferbackFromPendingDisbursementIslamicApplicationsThunk.fulfilled,
        (state, action) => {
          state.referBackFromPendingDisbursementIslamicApplicationList.isLoading = false;
          state.referBackFromPendingDisbursementIslamicApplicationList.data = action.payload;
        }
      )
      .addCase(
        getReferbackFromPendingDisbursementIslamicApplicationsThunk.rejected,
        (state, action) => {
          state.referBackFromPendingDisbursementIslamicApplicationList.isLoading = false;
          state.referBackFromPendingDisbursementIslamicApplicationList.isError = true;
          state.referBackFromPendingDisbursementIslamicApplicationList.errorMessage =
            action.payload;
          state.referBackFromPendingDisbursementIslamicApplicationList.data.data = [];
        }
      );

    builder
      .addCase(getApplicationDetailsThunk.pending, (state) => {
        state.applicationDetails.isLoading = true;
      })
      .addCase(getApplicationDetailsThunk.fulfilled, (state, action) => {
        state.applicationDetails.isLoading = false;
        state.applicationDetails.data = action.payload;
      })
      .addCase(getApplicationDetailsThunk.rejected, (state, action) => {
        state.applicationDetails.isLoading = false;
        state.applicationDetails.isError = true;
        state.applicationDetails.errorMessage = action.payload;
      });

    builder
      .addCase(getFilesByEntityIdThunk.pending, (state) => {
        state.applicationFiles.isLoading = true;
      })
      .addCase(getFilesByEntityIdThunk.fulfilled, (state, action) => {
        state.applicationFiles.isLoading = false;
        state.applicationFiles.data = groupBy(action.payload, (file: UploadedFile) =>
          file.fileType.name.toLowerCase()
        );
      })
      .addCase(getFilesByEntityIdThunk.rejected, (state, action) => {
        state.applicationFiles.isLoading = false;
        state.applicationFiles.isError = true;
        state.applicationFiles.errorMessage = action.payload;
      });

    builder
      .addCase(getInternalRemarksByApplicationIDThunk.pending, (state) => {
        state.internalRemarks.isLoading = true;
      })
      .addCase(getInternalRemarksByApplicationIDThunk.fulfilled, (state, action) => {
        state.internalRemarks.isLoading = false;
        state.internalRemarks.data = action.payload;
      })
      .addCase(getInternalRemarksByApplicationIDThunk.rejected, (state, action) => {
        state.internalRemarks.isLoading = false;
        state.internalRemarks.isError = true;
        state.internalRemarks.errorMessage = action.payload;
      });

    builder
      .addCase(getHistoryLogsByApplicationIDThunk.pending, (state) => {
        state.historyLogs.isLoading = true;
      })
      .addCase(getHistoryLogsByApplicationIDThunk.fulfilled, (state, action) => {
        state.historyLogs.isLoading = false;
        state.historyLogs.data = action.payload;
      })
      .addCase(getHistoryLogsByApplicationIDThunk.rejected, (state, action) => {
        state.historyLogs.isLoading = false;
        state.historyLogs.isError = true;
        state.historyLogs.errorMessage = action.payload;
      });

    builder
      .addCase(getHistoryLogsByApplicationIDAndAgreementIDThunk.pending, (state) => {
        state.historyLogs.isLoading = true;
      })
      .addCase(getHistoryLogsByApplicationIDAndAgreementIDThunk.fulfilled, (state, action) => {
        state.historyLogs.isLoading = false;
        state.historyLogs.data = action.payload;
      })
      .addCase(getHistoryLogsByApplicationIDAndAgreementIDThunk.rejected, (state, action) => {
        state.historyLogs.isLoading = false;
        state.historyLogs.isError = true;
        state.historyLogs.errorMessage = action.payload;
      });

    builder
      .addCase(getApplicantFilesThunk.pending, (state) => {
        state.applicantFiles.isLoading = true;
      })
      .addCase(getApplicantFilesThunk.fulfilled, (state, action) => {
        state.applicantFiles.isLoading = false;
        state.applicantFiles.data = action.payload;
      })
      .addCase(getApplicantFilesThunk.rejected, (state, action) => {
        state.applicantFiles.isLoading = false;
        state.applicantFiles.isError = true;
        state.applicantFiles.errorMessage = action.payload;
      });

    builder
      .addCase(getEkycFilesByEntityIdThunk.pending, (state) => {
        state.ekycFiles.isLoading = true;
      })
      .addCase(getEkycFilesByEntityIdThunk.fulfilled, (state, action) => {
        state.ekycFiles.isLoading = false;
        state.ekycFiles.data = action.payload;
      })
      .addCase(getEkycFilesByEntityIdThunk.rejected, (state, action) => {
        state.ekycFiles.isLoading = false;
        state.ekycFiles.isError = true;
        state.ekycFiles.errorMessage = action.payload;
      });

    builder
      .addCase(getApplicationCreditRecordThunk.pending, (state) => {
        state.creditRecords.isLoading = true;
      })
      .addCase(getApplicationCreditRecordThunk.fulfilled, (state, action) => {
        state.creditRecords.isLoading = false;
        state.creditRecords.data = action.payload;
      })
      .addCase(getApplicationCreditRecordThunk.rejected, (state, action) => {
        state.creditRecords.isLoading = false;
        state.creditRecords.isError = true;
        state.creditRecords.errorMessage = action.payload;
      });

    builder
      .addCase(globalSearchThunk.pending, (state) => {
        state.globalSearchApplicationList.isLoading = true;
      })
      .addCase(globalSearchThunk.fulfilled, (state, action) => {
        state.globalSearchApplicationList.isLoading = false;
        state.globalSearchApplicationList.data = action.payload;
      })
      .addCase(globalSearchThunk.rejected, (state, action) => {
        state.globalSearchApplicationList.isLoading = false;
        state.globalSearchApplicationList.isError = true;
        state.globalSearchApplicationList.errorMessage = action.payload;
      });

    builder
      .addCase(getIslamicApprovedReviewApplicationsThunk.pending, (state) => {
        state.approvedReviewIslamicApplicationList.isLoading = true;
      })
      .addCase(getIslamicApprovedReviewApplicationsThunk.fulfilled, (state, action) => {
        state.approvedReviewIslamicApplicationList.isLoading = false;
        state.approvedReviewIslamicApplicationList.data = action.payload;
      })
      .addCase(getIslamicApprovedReviewApplicationsThunk.rejected, (state, action) => {
        state.approvedReviewIslamicApplicationList.isLoading = false;
        state.approvedReviewIslamicApplicationList.isError = true;
        state.approvedReviewIslamicApplicationList.errorMessage = action.payload;
      });

    builder
      .addCase(getApplicationRecordByApplicationIdThunk.pending, (state) => {
        state.applicationRecord.isLoading = true;
      })
      .addCase(getApplicationRecordByApplicationIdThunk.fulfilled, (state, action) => {
        state.applicationRecord.isLoading = false;
        state.applicationRecord.data = {
          ...action.payload,
          pagination: {
            ...action.payload.pagination,
            current: action.payload.pagination.currentPage,
          },
        };
      })
      .addCase(getApplicationRecordByApplicationIdThunk.rejected, (state, action) => {
        state.applicationRecord.isLoading = false;
        state.applicationRecord.isError = true;
        state.applicationRecord.errorMessage = action.payload;
      });
  },
});

export const {
  applicationStateReset,
  applicationDetailsStateReset,
  setApplicationDetails,
  setGlobalSearchValue,
  setAccountStatementSearchValue,
} = islamicApplicationSlice.actions;

export default islamicApplicationSlice.reducer;
